import { Logger } from '../monitoring';

import { loadSegment } from './load-segment';
import { getSalesIntegrationsOptions, isGpcOptedOutOfSale, type AnalyticsConfig, type IdentifyParams, type TrackEvent } from './types';

const defaultConfig: AnalyticsConfig = {
  enabled: false,
  printEvents: true,
  segmentKey: '',
};

/**
 * Analytics singleton for tracking user events in auth-app.
 * Uses Segment as the analytics provider.
 */
export class Analytics {
  private static instance: Analytics;
  private config: AnalyticsConfig = defaultConfig;
  private initialized = false;
  private optedOutOfSale = false;

  private constructor() {
    if (isGpcOptedOutOfSale()) {
      this.disableSalesIntegrations();
    }
  }

  static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  /**
   * Initialize analytics with configuration.
   * Must be called before any tracking methods.
   */
  static init(config: AnalyticsConfig): void {
    const instance = Analytics.getInstance();
    instance.config = { ...defaultConfig, ...config };

    if (instance.config.enabled && instance.config.segmentKey) {
      loadSegment(instance.config.segmentKey);
      instance.initialized = true;
      instance.log('Analytics initialized');
    } else {
      instance.log('Analytics in print-only mode (not sending to Segment)');
    }
  }

  private get service() {
    return this.config.enabled && this.initialized ? (window.analytics ?? null) : null;
  }

  private log(...args: unknown[]): void {
    if (this.config.printEvents) {
      const message = `[Analytics] ${String(args[0] || '')}`;
      const context = args.length > 1 && typeof args[1] === 'object' && args[1] !== null ? (args[1] as object) : undefined;
      Logger.info(message, context);
    }
  }

  /**
   * Track an event following the analytics team spec.
   * TypeScript enforces correct properties based on event name.
   */
  track(event: TrackEvent): void {
    const enrichedProperties = {
      ...('properties' in event ? event.properties : {}),
      app: 'auth-app',
    };
    const integrations = getSalesIntegrationsOptions(this.optedOutOfSale);

    this.log(`track: ${event.eventName}`, enrichedProperties);

    if (this.service) {
      this.service.track(event.eventName, enrichedProperties, integrations && { integrations });
    }
  }

  /**
   * Identify a user after successful authentication.
   * Preserves the anonymous ID to link pre-login activity to the authenticated user.
   */
  identify({ userId, ...traits }: IdentifyParams): void {
    // Get the current anonymous ID from Segment to persist it across user ID changes
    const anonymousId = this.service?.user?.()?.anonymousId?.();
    const integrations = getSalesIntegrationsOptions(this.optedOutOfSale);
    const options = {
      ...(anonymousId && { anonymousId }),
      ...(integrations && { integrations }),
    };

    this.log('identify:', { userId, anonymousId, ...traits });

    if (this.service) {
      this.service.identify(userId, traits, options);
    }
  }

  /**
   * Track a page view. Called automatically by Segment on load,
   * but can be used for SPA navigation if needed.
   *
   * @param name - Page name
   */
  page(name?: string): void {
    const integrations = getSalesIntegrationsOptions(this.optedOutOfSale);

    this.log('page:', name);

    if (this.service) {
      this.service.page(name, undefined, integrations && { integrations });
    }
  }

  /**
   * Disables the CCPA-excluded sale/ad Segment destinations for all
   * subsequent track/identify/page calls. Called when the browser sends the
   * Global Privacy Control signal, or when the user's account has a recorded
   * "Do Not Sell" opt-out.
   */
  disableSalesIntegrations(): void {
    this.optedOutOfSale = true;
  }

  /**
   * Reset analytics state (e.g., on logout).
   */
  reset(): void {
    this.log('reset');

    if (this.service) {
      this.service.reset();
    }
  }

  /**
   * Execute callback when analytics is ready.
   */
  ready(callback: () => void): void {
    if (this.service) {
      this.service.ready(callback);
    } else {
      callback();
    }
  }
}
